import {Box, Button, Card, Grid, Theme, useMediaQuery} from '@material-ui/core';
import FormikTextField from '../../common/form/FormikTextField/FormikTextField';
import {useTranslation} from 'react-i18next';
import {APPOINTMENT_CALENDAR, ERROR, SUBSCRIPTION_PAYMENT_CONTINUE, USER_ACCOUNT_PAGE} from '../../../const/routes';
import React, {FormEvent, useContext, useState} from 'react';
import i18nNamespaces from '../../../const/i18nNamespaces';
import {useFormik} from 'formik';
import * as yup from 'yup';
import {usePayUSecureForms} from '../../common/payUSecureForms';
import {Loading, SubscriptionPaymentCard} from '../../index';
import subscriptionsProvider from '../../../services/subscriptionsProvider';
import {generatePath, useHistory} from 'react-router';
import axios from 'axios';
import {QbCardOwner} from '../../../types/individualPackages';
import FormMessage from '../../common/form/FormMessage/FormMessage';
import {desktop} from '../../../const/sizes';
import { accountApi } from '../../../api/account/accountApi';
import { insurancePackageErrorHandler } from '../../../utils/error';
import { AlertContext } from '../../../contexts/AlertContext';

const REQUIRED_STRING = 'requiredField';

type InsuranceDataCardProps = {
    individualPackageDraftId: number;
    qbCardOwner?: QbCardOwner;
    resellerCode?: string;
};
const InsuranceDataCard = ({
    individualPackageDraftId,
    qbCardOwner,
    resellerCode,
}: InsuranceDataCardProps) => {
    const {t} = useTranslation(i18nNamespaces.SUBSCRIPTION_PAYMENT);
    const {t: authT} = useTranslation(i18nNamespaces.AUTH);
    const {t: errorT} = useTranslation(i18nNamespaces.ERRORS);
    const {tokenize, sendCvv} = usePayUSecureForms();
    const history = useHistory();
    const isDesktop = useMediaQuery<Theme>(theme => theme.breakpoints.up('md'));
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [requiredCheckForced, setRequiredCheckForced] =
        useState<boolean>(false);
    const openAlert = useContext(AlertContext);

    const peselValidation = yup
            .string()
            .matches(/^[0-9]+$/, authT('peselShouldContainOnlyNumbers'))
            .length(11, authT('lengthShouldBe11chars'));

    const formik = useFormik({
        initialValues: {
            name: qbCardOwner?.name || '',
            surname: qbCardOwner?.surname || '',
            streetName: '',
            houseNumber: '',
            apartmentNumber: '',
            postcode: '',
            cityName: '',
            pesel: '',
            insuranceNumber: '',
        },
        validationSchema: yup.object({
            name: yup.string().required(authT(REQUIRED_STRING)),
            surname: yup.string().required(authT(REQUIRED_STRING)),
            streetName: yup.string().required(authT(REQUIRED_STRING)),
            houseNumber: yup.string().required(authT(REQUIRED_STRING)),
            postcode: yup.string().required(authT(REQUIRED_STRING)),
            cityName: yup.string().required(authT(REQUIRED_STRING)),
            pesel: peselValidation.required(authT(REQUIRED_STRING)),
            insuranceNumber: yup.string().required(authT(REQUIRED_STRING)),
        }),
        onSubmit: async values => {
           try {
                const {data} = await accountApi.registerInterRiskPatient({
                    name: values.name,
                    surname: values.surname,
                    pesel: values.pesel,
                    homeAddress: {
                        streetName: values.streetName,
                        houseNumber: values.houseNumber,
                        apartmentNumber: values.apartmentNumber,
                        postcode: values.postcode,
                        cityName: values.cityName,
                    },
                    insuranceNumber: values.insuranceNumber,
                });
                history.push(USER_ACCOUNT_PAGE + APPOINTMENT_CALENDAR);
           } catch(error){
                if (axios.isAxiosError(error)) {
                    openAlert({
                        severity: 'error',
                        message: errorT(insurancePackageErrorHandler(error.response?.data[0])),
                    });
                }
                console.error(error);
           }
        },
    });

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setRequiredCheckForced(true);
        formik.handleSubmit();
    };

    return (
        <form onSubmit={handleSubmit}>
            <Loading loading={loading} withBackdrop />
            <Card
                elevation={0}
                style={{
                    padding: desktop.contentSmallerSpacing,
                    marginBottom: desktop.contentSmallerSpacing,
                }}
            >
                <Box
                    style={{
                        fontWeight: 'bold',
                        marginBottom: desktop.contentSmallerSpacing,
                    }}
                >
                    {t('patientData')}
                </Box>
                <Grid container spacing={isDesktop ? 2 : 1}>
                    <Grid item sm={6} xs={12}>
                        <FormikTextField
                            form={formik}
                            label={authT('name') + ' *'}
                            fieldName={'name'}
                            noStyles
                            fullWidth
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <FormikTextField
                            form={formik}
                            label={authT('surname') + ' *'}
                            fieldName={'surname'}
                            noStyles
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormikTextField
                            form={formik}
                            label={'PESEL' + ' *'}
                            fieldName={'pesel'}
                            noStyles
                            fullWidth
                        />
                    </Grid>
                    <Grid item sm={12} lg={6} xs={12}>
                        <FormikTextField
                            form={formik}
                            label={authT('street') + ' *'}
                            fieldName={'streetName'}
                            noStyles
                            fullWidth
                        />
                    </Grid>
                    <Grid item sm={6} lg={3} xs={12}>
                        <FormikTextField
                            form={formik}
                            label={authT('houseNo') + ' *'}
                            fieldName={'houseNumber'}
                            noStyles
                            fullWidth
                        />
                    </Grid>
                    <Grid item sm={6} lg={3} xs={12}>
                        <FormikTextField
                            form={formik}
                            label={authT('apartmentNo')}
                            fieldName={'apartmentNumber'}
                            noStyles
                            fullWidth
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <FormikTextField
                            form={formik}
                            label={authT('cityName') + ' *'}
                            fieldName={'cityName'}
                            noStyles
                            fullWidth
                        />
                    </Grid>
                    <Grid item sm={6} lg={3} xs={12}>
                        <FormikTextField
                            form={formik}
                            label={authT('postcode') + ' *'}
                            fieldName={'postcode'}
                            noStyles
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </Card>
            <Card
                elevation={0}
                style={{
                    padding: desktop.contentSmallerSpacing,
                    marginBottom: desktop.contentSmallerSpacing,
                }}
            >
                <Box
                    style={{
                        fontWeight: 'bold',
                        marginBottom: desktop.contentSmallerSpacing,
                    }}
                >
                    {t('insuranceData')}
                </Box>
                <FormikTextField
                    form={formik}
                    label={t('insuranceNumber') + ' *'}
                    fieldName={'insuranceNumber'}
                    noStyles
                    fullWidth
                />
            </Card>
            <Card
                elevation={0}
                style={{
                    padding: desktop.contentSmallerSpacing,
                    marginBottom: desktop.contentSmallerSpacing,
                }}
            >
                <FormMessage message={errorMessage} mb={1} />
                <Button
                    variant={'contained'}
                    color={'secondary'}
                    type={'submit'}
                    style={{fontWeight: 'bold'}}
                    fullWidth
                >
                    {t('activatePackage')}
                </Button>
            </Card>
        </form>
    );
};

export default InsuranceDataCard;
